import {useIsFeatureStateEnabled} from '@github-ui/pull-requests/use-feature-state'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useMemo, useState, type PropsWithChildren} from 'react'

import {EffectiveEntryStatus} from '../../../helpers/effective-entry-status'
import {calculateMergeability} from '../../../helpers/mergeability-helpers'
import {MergeAction} from '../../../types'
import {deriveMergeControlModel} from './merge-control/derive-merge-control-model'
import {
  MergeControlActionsContext,
  MergeControlInputsContext,
  type MergeControlProps,
  MergeControlStateContext,
} from './merge-control/merge-control-context'
import {BypassToggle} from './merge-control/BypassToggle'
import {Instructions} from './merge-control/Instructions'
import {MergeComponent} from './merge-control/MergeComponent'

export type {MergeControlProps} from './merge-control/merge-control-context'

function MergeControlRoot({children, ...mergeSectionProps}: PropsWithChildren<MergeControlProps>) {
  const {sendAnalyticsEvent} = useAnalytics()
  const [isAdminBypassToggleChecked, setIsAdminBypassToggleChecked] = useState(false)
  const isPullRequestStacksAutoMergeEnabled = useIsFeatureStateEnabled('pullRequestStacksAutoMerge')

  const {
    isConfirmingMergeInfo,
    isStackMergeInProgress = false,
    isStacked,
    mergeRequirements,
    pullRequest,
    stackedEntries,
    stackedEntriesToMergeCount,
    status,
  } = mergeSectionProps

  const {
    advisoryWorkspace,
    autoMergeRequest,
    id,
    mergeStateStatus,
    viewerCanAdminBypassMergeRequirements,
    viewerCanEnableAutoMerge,
    viewerMergeActions,
  } = pullRequest

  const isMergeable = calculateMergeability(mergeRequirements.state, status, mergeStateStatus)
  const isAsyncStackMerge = isStacked === true

  const isMergeQueueEnabled =
    viewerMergeActions.find(({name}) => name === MergeAction.MERGE_QUEUE)?.allowableStatus === 'ALLOWED'
  const isDirectMergeEnabled =
    viewerMergeActions.find(({name}) => name === MergeAction.DIRECT_MERGE)?.allowableStatus === 'ALLOWED'

  const stackSize = stackedEntries?.length
  const currentStackEntryIndex = stackedEntries?.findIndex(entry => entry.position === 'CURRENT')
  const currentStackEntryPositionNumber =
    currentStackEntryIndex !== undefined && currentStackEntryIndex >= 0 ? currentStackEntryIndex + 1 : undefined

  // Stacked PRs only ever show the bypass checkbox for the bottom-most unmerged
  // entry in the stack. Queued entries are excluded from stackedEntriesToMergeCount,
  // so check them separately before treating a count of one as the bottom-most entry.
  const isStackedPrEligibleForBypass =
    !isStacked ||
    (stackedEntriesToMergeCount === 1 &&
      !stackedEntries?.some(
        entry => entry.position === 'BEFORE' && entry.effectiveEntryStatus === EffectiveEntryStatus.Queued,
      ))

  // Active auto-merge requests never offer bypass. When merge queue is enabled, bypass eligibility does not depend
  // on mergeability. Otherwise, mergeability keeps the toggle responsive to live status-check updates.
  const isBypassAllowed =
    viewerCanAdminBypassMergeRequirements &&
    !autoMergeRequest &&
    isStackedPrEligibleForBypass &&
    (isMergeQueueEnabled || !isMergeable)

  const mergeControlModel = deriveMergeControlModel({
    autoMergeRequest,
    isAdminBypassToggleChecked,
    isAsyncStackMerge,
    isBypassAllowed,
    isDirectMergeEnabled,
    isMergeQueueEnabled,
    isPullRequestStacksAutoMergeEnabled,
    isStackMergeBlocked: mergeSectionProps.mergeCurrentlyBlockedForStacks,
    isStackMergeInProgress,
    isStacked,
    viewerCanEnableAutoMerge,
  })

  const onToggleAdminBypass = useCallback(() => {
    setIsAdminBypassToggleChecked(previous => !previous)
  }, [setIsAdminBypassToggleChecked])

  const showBypassToggle = isBypassAllowed && !isConfirmingMergeInfo

  const state = useMemo(
    () => ({
      isAdminBypassToggleChecked,
      isBypassAllowed,
      isConfirmingMergeInfo,
      isMergeable,
      isMergeQueueEnabled,
      mergeControlModel,
      showBypassToggle,
      currentStackEntryPositionNumber,
      stackSize,
    }),
    [
      isAdminBypassToggleChecked,
      isBypassAllowed,
      isConfirmingMergeInfo,
      isMergeable,
      isMergeQueueEnabled,
      mergeControlModel,
      showBypassToggle,
      currentStackEntryPositionNumber,
      stackSize,
    ],
  )

  useEffect(() => {
    if (isMergeable) {
      sendAnalyticsEvent('merge_section.view_ready_to_merge', 'MERGEBOX_MERGE_SECTION', {pullRequestId: id})
    }
  }, [isMergeable, sendAnalyticsEvent, id])

  // We can skip rendering the merge control for advisory workspaces and synchronous stack merges.
  if (advisoryWorkspace || (isStackMergeInProgress && !isAsyncStackMerge)) {
    return null
  }

  return (
    <MergeControlInputsContext value={mergeSectionProps}>
      <MergeControlActionsContext value={onToggleAdminBypass}>
        <MergeControlStateContext value={state}>{children}</MergeControlStateContext>
      </MergeControlActionsContext>
    </MergeControlInputsContext>
  )
}

export const MergeControl = Object.assign(MergeControlRoot, {
  BypassToggle,
  Control: MergeComponent,
  Instructions,
})
